<template>
  <header class="pixso-nav" @mouseleave="scheduleDesktopClose">
    <div class="pixso-nav__inner">
      <router-link class="pixso-nav__logo-link" to="/" :aria-label="t('nav.logoAria')">
        <img class="pixso-nav__logo" src="@/assets/images/svgs/logo.dark.svg" :alt="t('nav.logoAlt')" />
      </router-link>

      <nav class="pixso-nav__desktop" :aria-label="t('nav.mainNavigation')">
        <div
          v-for="item in topNavItems"
          :key="item.key"
          class="pixso-nav__desktop-item"
          :class="{ 'pixso-nav__desktop-item--active': isTopNavEmphasized(item) }"
          @mouseenter="handleTopEnter(item)"
        >
          <router-link v-if="item.to" class="pixso-nav__desktop-link" :to="item.to" :data-text="item.label">
            <span class="pixso-nav__desktop-link-text">{{ item.label }}</span>
          </router-link>
          <button v-else type="button" class="pixso-nav__desktop-link pixso-nav__desktop-link--button" :data-text="item.label">
            <span class="pixso-nav__desktop-link-text">{{ item.label }}</span>
          </button>
        </div>
      </nav>

      <button
        type="button"
        class="pixso-nav__locale-switch"
        :aria-label="localeSwitchLabel"
        @click="toggleLocale"
      >
        <svg class="pixso-nav__locale-icon" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9.5" />
          <ellipse cx="12" cy="12" rx="4.2" ry="9.5" />
          <path d="M2.5 12h19" />
          <path d="M12 2.5c2.6 2.4 4.2 5.7 4.2 9.5S14.6 19.6 12 22" />
          <path d="M12 2.5c-2.6 2.4-4.2 5.7-4.2 9.5S9.4 19.6 12 22" />
        </svg>
        <span class="pixso-nav__locale-text">{{ localeDisplayText }}</span>
      </button>

      <div class="pixso-nav__mobile-actions">
        <button
          type="button"
          class="pixso-nav__mobile-locale-switch"
          :aria-label="localeSwitchLabel"
          @click="toggleLocale"
        >
          <svg class="pixso-nav__locale-icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9.5" />
            <ellipse cx="12" cy="12" rx="4.2" ry="9.5" />
            <path d="M2.5 12h19" />
            <path d="M12 2.5c2.6 2.4 4.2 5.7 4.2 9.5S14.6 19.6 12 22" />
            <path d="M12 2.5c-2.6 2.4-4.2 5.7-4.2 9.5S9.4 19.6 12 22" />
          </svg>
          <span class="pixso-nav__locale-text">{{ localeDisplayText }}</span>
        </button>

        <button
          type="button"
          class="pixso-nav__mobile-toggle"
          :aria-expanded="isMobileMenuOpen"
          :aria-label="t('nav.toggleMenu')"
          @click="toggleMobileMenu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </div>

    <transition name="pixso-nav-dropdown">
      <div
        v-if="activeDesktopMenu"
        class="pixso-nav__dropdown-viewport"
        :style="{ height: `${activeDesktopMenu.dropdown.height}px` }"
        @mouseenter="clearDesktopCloseTimer"
        @mouseleave="scheduleDesktopClose"
      >
        <div
          class="pixso-nav__dropdown-content"
          :class="{
            'pixso-nav__dropdown-content--compact': activeDesktopMenu.key === 'about',
            'pixso-nav__dropdown-content--left': activeDesktopMenu.key === 'products'
          }"
          :style="desktopDropdownStyle"
        >
          <template v-if="activeDesktopMenu.key === 'products'">
            <div class="pixso-nav__panel-column pixso-nav__panel-column--primary">
              <router-link
                v-for="section in productSections"
                :key="section.key"
                class="pixso-nav__panel-link"
                :class="{ 'pixso-nav__panel-link--active': activeProductSection?.key === section.key }"
                :to="section.to"
                :data-text="section.label"
                @mouseenter="setActiveProductSection(section.key)"
              >
                <span class="pixso-nav__panel-link-text">{{ section.label }}</span>
              </router-link>
            </div>

            <div
              v-if="activeProductSection?.children?.length"
              class="pixso-nav__panel-column"
            >
              <template v-for="child in activeProductSection.children" :key="child.key">
                <router-link
                  v-if="child.to"
                  class="pixso-nav__panel-link"
                  :class="{ 'pixso-nav__panel-link--active': activeProductChild?.key === child.key }"
                  :to="child.to"
                  :data-text="child.label"
                  @mouseenter="setActiveProductChild(child.key)"
                >
                  <span class="pixso-nav__panel-link-text">{{ child.label }}</span>
                </router-link>
                <button
                  v-else
                  type="button"
                  class="pixso-nav__panel-link pixso-nav__panel-link--button"
                  :class="{ 'pixso-nav__panel-link--active': activeProductChild?.key === child.key }"
                  :data-text="child.label"
                  @mouseenter="setActiveProductChild(child.key)"
                >
                  <span class="pixso-nav__panel-link-text">{{ child.label }}</span>
                </button>
              </template>
            </div>

            <div
              v-if="activeProductGrandchildren.length"
              class="pixso-nav__panel-column"
            >
              <router-link
                v-for="grandchild in activeProductGrandchildren"
                :key="grandchild.key"
                class="pixso-nav__panel-link pixso-nav__panel-link--grandchild"
                :class="{ 'pixso-nav__panel-link--active': isGrandchildRouteActive(grandchild) }"
                :to="grandchild.to"
              >
                {{ grandchild.label }}
              </router-link>
            </div>
          </template>

          <template v-else>
            <div class="pixso-nav__panel-column pixso-nav__panel-column--compact">
              <router-link
                v-for="link in aboutLinks"
                :key="link.key"
                class="pixso-nav__panel-link"
                :class="{ 'pixso-nav__panel-link--active': isAboutLinkActive(link) }"
                :to="link.to"
                :data-text="link.label"
              >
                <span class="pixso-nav__panel-link-text">{{ link.label }}</span>
              </router-link>
            </div>
          </template>
        </div>
      </div>
    </transition>

    <transition name="pixso-nav-fade">
        <button
        v-if="isMobileMenuOpen"
        type="button"
        class="pixso-nav__mobile-backdrop"
        :aria-label="t('nav.closeMenu')"
        @click="closeMobileMenu"
      ></button>
    </transition>

    <transition name="pixso-nav-mobile">
      <div v-if="isMobileMenuOpen" class="pixso-nav__mobile-panel">
        <div v-for="item in topNavItems" :key="item.key" class="pixso-nav__mobile-item">
          <div class="pixso-nav__mobile-row">
            <router-link
              v-if="item.to && !item.dropdown"
              class="pixso-nav__mobile-link"
              :class="{ 'pixso-nav__mobile-link--active': isTopNavActive(item) }"
              :to="item.to"
              @click="closeMobileMenu"
            >
              {{ item.label }}
            </router-link>
            <button
              v-else
              type="button"
              class="pixso-nav__mobile-link pixso-nav__mobile-link--button"
              :class="{ 'pixso-nav__mobile-link--active': isTopNavActive(item) }"
              @click="toggleMobileGroup(item.key)"
            >
              {{ item.label }}
            </button>
            <button
              v-if="item.dropdown"
              type="button"
              class="pixso-nav__mobile-arrow"
              :class="{ 'pixso-nav__mobile-arrow--open': isMobileGroupOpen(item.key) }"
              :aria-expanded="isMobileGroupOpen(item.key)"
              @click.stop="toggleMobileGroup(item.key)"
            >
              <span></span>
            </button>
          </div>

          <div v-if="item.key === 'products' && isMobileGroupOpen(item.key)" class="pixso-nav__mobile-submenu">
            <div v-for="section in productSections" :key="section.key" class="pixso-nav__mobile-subitem">
              <div class="pixso-nav__mobile-row pixso-nav__mobile-row--sub">
                <button
                  v-if="section.children?.length"
                  type="button"
                  class="pixso-nav__mobile-link pixso-nav__mobile-link--sub pixso-nav__mobile-link--button"
                  :class="{ 'pixso-nav__mobile-link--active': isProductSectionActive(section) }"
                  :aria-expanded="isMobileSectionOpen(section.key)"
                  @click="toggleMobileSection(section.key)"
                >
                  {{ section.label }}
                </button>
                <router-link
                  v-else
                  class="pixso-nav__mobile-link pixso-nav__mobile-link--sub"
                  :to="section.to"
                  @click="closeMobileMenu"
                >
                  {{ section.label }}
                </router-link>
                <button
                  v-if="section.children?.length"
                  type="button"
                  class="pixso-nav__mobile-arrow pixso-nav__mobile-arrow--small"
                  :class="{ 'pixso-nav__mobile-arrow--open': isMobileSectionOpen(section.key) }"
                  :aria-expanded="isMobileSectionOpen(section.key)"
                  @click.stop="toggleMobileSection(section.key)"
                >
                  <span></span>
                </button>
              </div>

              <div v-if="section.children?.length && isMobileSectionOpen(section.key)" class="pixso-nav__mobile-children">
                <div v-for="child in section.children" :key="child.key" class="pixso-nav__mobile-child-block">
                  <div class="pixso-nav__mobile-row pixso-nav__mobile-row--child">
                    <button
                      v-if="child.children?.length"
                      type="button"
                      class="pixso-nav__mobile-link pixso-nav__mobile-link--child pixso-nav__mobile-link--button"
                      :class="{ 'pixso-nav__mobile-link--active': isProductChildActive(child) }"
                      :aria-expanded="isMobileSectionOpen(child.key)"
                      @click="toggleMobileSection(child.key)"
                    >
                      {{ child.label }}
                    </button>
                    <router-link
                      v-else
                      class="pixso-nav__mobile-link pixso-nav__mobile-link--child"
                      :class="{ 'pixso-nav__mobile-link--active': isProductChildActive(child) }"
                      :to="child.to"
                      @click="closeMobileMenu"
                    >
                      {{ child.label }}
                    </router-link>
                    <button
                      v-if="child.children?.length"
                      type="button"
                      class="pixso-nav__mobile-arrow pixso-nav__mobile-arrow--small"
                      :class="{ 'pixso-nav__mobile-arrow--open': isMobileSectionOpen(child.key) }"
                      :aria-expanded="isMobileSectionOpen(child.key)"
                      @click.stop="toggleMobileSection(child.key)"
                    >
                      <span></span>
                    </button>
                  </div>
                  <div v-if="child.children?.length && isMobileSectionOpen(child.key)" class="pixso-nav__mobile-grandchildren">
                    <router-link
                      v-for="grandchild in child.children"
                      :key="grandchild.key"
                      class="pixso-nav__mobile-link pixso-nav__mobile-link--grandchild"
                      :to="grandchild.to"
                      @click="closeMobileMenu"
                    >
                      {{ grandchild.label }}
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="item.key === 'about' && isMobileGroupOpen(item.key)"
            class="pixso-nav__mobile-submenu pixso-nav__mobile-submenu--about"
          >
            <router-link
              v-for="link in aboutLinks"
              :key="link.key"
              class="pixso-nav__mobile-link pixso-nav__mobile-link--sub"
              :to="link.to"
              @click="closeMobileMenu"
            >
              {{ link.label }}
            </router-link>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

const route = useRoute()
const { locale, t } = useI18n()

const topNavItems = computed(() => [
  {
    key: 'home',
    label: t('nav.home'),
    to: '/',
    match: (path) => path === '/'
  },
  {
    key: 'products',
    label: t('nav.products'),
    to: '',
    match: (path) => /^\/(product|service|solution)(\/|$)/.test(path),
    dropdown: {
      height: 360
    }
  },
  {
    key: 'delivery',
    label: t('nav.delivery'),
    to: '/deliver',
    match: (path) => /^\/deliver(\/|$)/.test(path)
  },
    {
      key: 'about',
      label: t('nav.about'),
      to: '/about',
      match: (path) => /^\/about(\/|$)/.test(path),
      dropdown: {
        offset: 878,
        height: 228
      }
    }
])

const productSections = computed(() => [
  {
    key: 'automotive',
    label: t('nav.submenu.products.automotive'),
    to: '/product/automotive',
    children: [
      { key: 'imu', label: t('nav.submenu.products.imu_series'), to: '/product/automotive/imu' },
      { key: 'gnss', label: t('nav.submenu.products.gnss_series'), to: '/product/automotive/gnss' },
      { key: 'pbox', label: t('nav.submenu.products.pbox_series'), to: '/product/automotive/pbox' },
      { key: 'ins', label: t('nav.submenu.products.ins_series'), to: '/product/automotive/ins' }
    ]
  },
  {
    key: 'industry',
    label: t('nav.submenu.products.industry'),
    to: '/product/industry',
    children: [
      { key: 'industry-nav', label: t('nav.submenu.products.industry_nav'), to: '/product/industry/positioning' },
      { key: 'industry-terminal', label: t('nav.submenu.products.domain_control'), to: '/product/industry/domain-control' }
    ]
  },
  {
    key: 'solutions',
    label: t('nav.submenu.products.solutions'),
    to: '/solution',
    children: [
      { key: 'smart-driving', label: t('nav.submenu.solutions.smart_driving'), to: '/solution/smart-driving' },
      {
        key: 'inspection',
        label: t('nav.submenu.solutions.inspection'),
        to: '/solution/collaborative-inspection',
        children: [
          { key: 'inspection-public', label: t('nav.submenu.solutions.inspection_public'), to: '/solution/public-security-inspection' },
          { key: 'inspection-industry', label: t('nav.submenu.solutions.inspection_industry'), to: '/solution/industrial-inspection' },
          { key: 'inspection-tunnel', label: t('nav.submenu.solutions.inspection_tunnel'), to: '/solution/tunnel-corridor-inspection' }
        ]
      },
      { key: 'machinery', label: t('nav.submenu.solutions.traditional_machinery'), to: '/solution/traditional-machinery-intelligent-operation' },
      { key: 'logistics', label: t('nav.submenu.solutions.collaborative_logistics'), to: '/solution/air-ground-collaboration' }
    ]
  },
  {
    key: 'software-algorithm',
    label: t('nav.softwareAlgorithm'),
    to: '/service/software',
    children: []
  }
])

const aboutLinks = computed(() => [
  { key: 'contact', label: t('nav.submenu.about.contact'), to: '/about/contact' },
  { key: 'company', label: t('nav.submenu.about.company'), to: '/about' }
])

const activeDesktopMenuKey = ref('')
const activeProductSectionKey = ref('automotive')
const activeProductChildKey = ref('imu')
const isMobileMenuOpen = ref(false)
const openMobileGroupKeys = ref([])
const openMobileSectionKeys = ref([])
const desktopCloseTimer = ref(null)

const localeDisplayText = computed(() => (locale.value === 'zh' ? 'EN' : '中文'))
const localeSwitchLabel = computed(() => (locale.value === 'zh' ? t('nav.switchToEnglish') : t('nav.switchToChinese')))

const activeDesktopMenu = computed(() => topNavItems.value.find((item) => item.key === activeDesktopMenuKey.value && item.dropdown) || null)
const activeProductSection = computed(() => productSections.value.find((section) => section.key === activeProductSectionKey.value) || productSections.value[0])
const activeProductChild = computed(() => {
  const children = activeProductSection.value?.children || []
  return children.find((child) => child.key === activeProductChildKey.value) || children[0] || null
})
const activeProductGrandchildren = computed(() => activeProductChild.value?.children || [])
const desktopDropdownStyle = computed(() => {
  if (activeDesktopMenu.value?.key === 'products') {
    return {
      marginLeft: 'max(24px, calc((100vw - 1152px) / 2))'
    }
  }

  if (activeDesktopMenu.value?.key === 'about') {
    const offset = -209
    return {
      marginLeft: `max(24px, 50vw + ${offset}px)`
    }
  }

  return null
})
const clearDesktopCloseTimer = () => {
  if (desktopCloseTimer.value) {
    window.clearTimeout(desktopCloseTimer.value)
    desktopCloseTimer.value = null
  }
}

const closeDesktopMenu = () => {
  clearDesktopCloseTimer()
  activeDesktopMenuKey.value = ''
}

const scheduleDesktopClose = () => {
  clearDesktopCloseTimer()
  desktopCloseTimer.value = window.setTimeout(() => {
    activeDesktopMenuKey.value = ''
  }, 120)
}

const setDefaultProductState = () => {
  if (route.path.startsWith('/product/industry')) {
    activeProductSectionKey.value = 'industry'
    activeProductChildKey.value = route.path.startsWith('/product/industry/domain-control') ? 'industry-terminal' : 'industry-nav'
    return
  }

  if (route.path.startsWith('/solution')) {
    activeProductSectionKey.value = 'solutions'

    if (
      route.path.startsWith('/solution/public-security-inspection') ||
      route.path.startsWith('/solution/industrial-inspection') ||
      route.path.startsWith('/solution/tunnel-corridor-inspection')
    ) {
      activeProductChildKey.value = 'inspection'
      return
    }

    if (route.path.startsWith('/solution/traditional-machinery-intelligent-operation')) {
      activeProductChildKey.value = 'machinery'
      return
    }
    if (route.path.startsWith('/solution/air-ground-collaboration')) {
      activeProductChildKey.value = 'logistics'
      return
    }

    activeProductChildKey.value = 'smart-driving'
    return
  }

  activeProductSectionKey.value = 'automotive'

  if (route.path.startsWith('/product/imu') || route.path.startsWith('/product/automotive/imu')) {
    activeProductChildKey.value = 'imu'
  } else if (route.path.startsWith('/product/automotive/gnss')) {
    activeProductChildKey.value = 'gnss'
  } else if (route.path.includes('/automotive/imu/')) {
    activeProductChildKey.value = 'imu'
  } else if (route.path.startsWith('/product/ins') || route.path.startsWith('/product/automotive/ins')) {
    activeProductChildKey.value = 'ins'
  } else if (route.path.startsWith('/product/pbox') || route.path.startsWith('/product/automotive/pbox')) {
    activeProductChildKey.value = 'pbox'
  } else {
    activeProductChildKey.value = 'imu'
  }
}

const handleTopEnter = (item) => {
  clearDesktopCloseTimer()

  if (!item.dropdown) {
    activeDesktopMenuKey.value = ''
    return
  }

  activeDesktopMenuKey.value = item.key

  if (item.key === 'products') {
    setDefaultProductState()
  }
}

const setActiveProductSection = (sectionKey) => {
  activeProductSectionKey.value = sectionKey
  const section = productSections.value.find((item) => item.key === sectionKey)
  activeProductChildKey.value = section?.children?.[0]?.key || ''
}

const setActiveProductChild = (childKey) => {
  activeProductChildKey.value = childKey
}

const isTopNavActive = (item) => item.match(route.path)
const isTopNavEmphasized = (item) => (activeDesktopMenuKey.value ? activeDesktopMenuKey.value === item.key : isTopNavActive(item))
const isGrandchildRouteActive = (grandchild) => grandchild.to !== '/solution' && route.path === grandchild.to
const isProductSectionActive = (section) => section.to && route.path.startsWith(section.to)
const isProductChildActive = (child) => {
  if (child.children?.some((grandchild) => route.path === grandchild.to)) {
    return true
  }

  return child.to && route.path.startsWith(child.to)
}
const isAboutLinkActive = (link) => {
  if (link.key === 'contact') {
    return route.path === '/about/contact'
  }
  return route.path === '/about'
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const toggleLocale = () => {
  const nextLocale = locale.value === 'zh' ? 'en' : 'zh'
  locale.value = nextLocale
  localStorage.setItem('locale', nextLocale)
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

const toggleKey = (targetRef, key) => {
  if (targetRef.value.includes(key)) {
    targetRef.value = targetRef.value.filter((item) => item !== key)
    return
  }

  targetRef.value = [...targetRef.value, key]
}

const isMobileGroupOpen = (key) => openMobileGroupKeys.value.includes(key)
const isMobileSectionOpen = (key) => openMobileSectionKeys.value.includes(key)

const toggleMobileGroup = (key) => {
  toggleKey(openMobileGroupKeys, key)
}

const toggleMobileSection = (key) => {
  toggleKey(openMobileSectionKeys, key)
}

watch(
  () => route.path,
  () => {
    closeDesktopMenu()
    closeMobileMenu()
  }
)

watch(isMobileMenuOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
})

onBeforeUnmount(() => {
  clearDesktopCloseTimer()
  document.body.style.overflow = ''
})
</script>

<style scoped>
.pixso-nav,
.pixso-nav * {
  box-sizing: border-box;
}

.pixso-nav * {
  width: auto;
}

.pixso-nav {
  --pixso-nav-content-max: 1152px;
  --pixso-nav-logo-width: 229px;
  --pixso-nav-logo-height: 31px;
  --pixso-nav-logo-mobile-width: 172px;
  --pixso-nav-mobile-panel-padding-y: var(--pixso-gap-sm);
  --pixso-nav-mobile-row-padding-y: 18px;
  --pixso-nav-mobile-sub-row-padding-y: 14px;
  --pixso-nav-mobile-child-padding-y: 10px;
  --pixso-nav-mobile-grandchild-padding-y: 8px;
  width: 100%;
  height: var(--pixso-nav-height);
  background: var(--pixso-color-surface);
  position: sticky;
  top: 0;
  z-index: 120;
}

.pixso-nav__inner {
  width: min(calc(100% - var(--pixso-gap-lg)), var(--pixso-nav-content-max));
  height: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: auto 1fr auto 1fr;
  align-items: center;
}

.pixso-nav__logo-link,
.pixso-nav__desktop-link,
.pixso-nav__panel-link,
.pixso-nav__mobile-link {
  text-decoration: none;
  color: var(--pixso-color-text);
}

.pixso-nav__logo-link {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.pixso-nav__logo {
  width: var(--pixso-nav-logo-width);
  height: var(--pixso-nav-logo-height);
  object-fit: contain;
  display: block;
}

.pixso-nav__desktop {
  grid-column: 3;
  width: 560px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pixso-nav__desktop-item {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.pixso-nav__desktop-item--active::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 12px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--pixso-color-text);
  transform: translateX(-50%);
}

.pixso-nav__desktop-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
  font-weight: 400;
  transition: opacity 0.2s ease;
}

.pixso-nav__desktop-link::before {
  content: attr(data-text);
  font-weight: 700;
  visibility: hidden;
}

.pixso-nav__desktop-link-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.pixso-nav__desktop-link--button,
.pixso-nav__panel-link--button,
.pixso-nav__mobile-link--button {
  padding: 0;
  border: 0;
  background: transparent;
  font-family: inherit;
  cursor: pointer;
}

.pixso-nav__desktop-item--active .pixso-nav__desktop-link,
.pixso-nav__desktop-link:hover {
  font-weight: 700;
}

.pixso-nav__locale-switch {
  position: absolute;
  top: 18px;
  right: max(64px, calc((100vw - var(--pixso-page-max)) / 2 + 102px));
  display: inline-flex;
  align-items: center;
  gap: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--pixso-color-text);
  font-family: "Montserrat", var(--pixso-font-cn);
  cursor: pointer;
}

.pixso-nav__locale-icon {
  display: block;
  width: 36px;
  height: 36px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1;
  opacity: 0.95;
}

.pixso-nav__locale-text {
  display: inline-flex;
  align-items: center;
  padding: 6px 4px;
  font-size: var(--pixso-fs-20);
  line-height: var(--pixso-lh-20);
  font-weight: 500;
  white-space: nowrap;
}

.pixso-nav__mobile-toggle {
  display: none;
}

.pixso-nav__mobile-toggle span {
  width: 20px;
  height: 2px;
  border-radius: 999px;
  background: var(--pixso-color-text);
  display: block;
}

.pixso-nav__mobile-actions {
  display: none;
}

.pixso-nav__dropdown-viewport {
  position: absolute;
  left: 50%;
  top: 100%;
  width: 100vw;
  transform: translateX(-50%);
  background: var(--pixso-color-surface);
  overflow: hidden;
}

.pixso-nav__dropdown-content {
  width: min(calc(100% - var(--pixso-gap-lg)), var(--pixso-nav-content-max));
  margin: 0 auto;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: var(--pixso-page-padding);
  padding: var(--pixso-fs-32) var(--pixso-gap-md) 0;
}

.pixso-nav__dropdown-content--compact {
  gap: 0;
}

.pixso-nav__dropdown-content--left {
  width: auto;
  margin-right: 0;
  justify-content: flex-start;
  padding-left: 0;
}

.pixso-nav__panel-column {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.pixso-nav__panel-column--compact {
  padding-bottom: var(--pixso-fs-32);
}

.pixso-nav__panel-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 66px;
  font-size: var(--pixso-fs-24);
  line-height: var(--pixso-lh-24);
  font-weight: 400;
  white-space: nowrap;
  transition: opacity 0.2s ease;
}

.pixso-nav__panel-link::before {
  content: attr(data-text);
  font-weight: 700;
  visibility: hidden;
}

.pixso-nav__panel-link-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.pixso-nav__panel-link--grandchild {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.pixso-nav__panel-link--active,
.pixso-nav__panel-link:hover {
  font-weight: 700;
}

.pixso-nav__mobile-backdrop,
.pixso-nav__mobile-panel {
  display: none;
}

.pixso-nav-dropdown-enter-active,
.pixso-nav-dropdown-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.pixso-nav-dropdown-enter-from,
.pixso-nav-dropdown-leave-to {
  opacity: 0;
  transform: translate(-50%, -8px);
}

.pixso-nav-fade-enter-active,
.pixso-nav-fade-leave-active {
  transition: opacity 0.2s ease;
}

.pixso-nav-fade-enter-from,
.pixso-nav-fade-leave-to {
  opacity: 0;
}

.pixso-nav-mobile-enter-active,
.pixso-nav-mobile-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.pixso-nav-mobile-enter-from,
.pixso-nav-mobile-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 1200px) {
  .pixso-nav__inner {
    width: min(calc(100% - var(--pixso-gap-lg)), 100%);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .pixso-nav__logo {
    width: var(--pixso-nav-logo-mobile-width);
    height: auto;
  }

  .pixso-nav__desktop,
  .pixso-nav__dropdown-viewport {
    display: none;
  }

  .pixso-nav__locale-switch {
    display: none;
  }

  .pixso-nav__mobile-actions {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }

  .pixso-nav__mobile-locale-switch {
    display: inline-flex;
    align-items: center;
    gap: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--pixso-color-text);
    font-family: "Montserrat", var(--pixso-font-cn);
    cursor: pointer;
  }

  .pixso-nav__mobile-locale-switch .pixso-nav__locale-icon {
    width: 28px;
    height: 28px;
  }

  .pixso-nav__mobile-locale-switch .pixso-nav__locale-text {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .pixso-nav__mobile-toggle {
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    background: transparent;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    cursor: pointer;
    flex: 0 0 auto;
  }

  .pixso-nav__mobile-toggle span {
    width: 18px;
  }

  .pixso-nav__mobile-backdrop {
    display: block;
    position: fixed;
    inset: var(--pixso-nav-height) 0 0;
    border: 0;
    background: rgba(0, 0, 0, 0.42);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    z-index: 118;
  }

  .pixso-nav__mobile-panel {
    display: block;
    position: fixed;
    inset: var(--pixso-nav-height) 0 auto 0;
    max-height: calc(100vh - var(--pixso-nav-height));
    overflow-y: auto;
    padding: var(--pixso-nav-mobile-panel-padding-y) var(--pixso-gap-md) var(--pixso-gap-md);
    background: rgba(22, 22, 23, 0.98);
    z-index: 119;
  }

  .pixso-nav__mobile-item {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .pixso-nav__mobile-row {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--pixso-fs-16);
    padding: var(--pixso-nav-mobile-row-padding-y) 0;
  }

  .pixso-nav__mobile-row--sub {
    padding: var(--pixso-nav-mobile-sub-row-padding-y) 0;
  }

  .pixso-nav__mobile-row--child {
    padding: 0;
  }

  .pixso-nav__mobile-link {
    display: inline-flex;
    align-items: center;
    text-align: left;
    font-size: var(--pixso-fs-18);
    line-height: var(--pixso-lh-18);
    font-weight: 500;
    color: var(--pixso-color-text);
  }

  .pixso-nav__mobile-link--active {
    font-weight: 700;
  }

  .pixso-nav__mobile-link--sub {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .pixso-nav__mobile-link--child {
    padding: var(--pixso-nav-mobile-child-padding-y) 0;
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
    color: rgba(255, 255, 255, 0.92);
  }

  .pixso-nav__mobile-link--grandchild {
    padding: var(--pixso-nav-mobile-grandchild-padding-y) 0;
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
    color: rgba(255, 255, 255, 0.72);
  }

  .pixso-nav__mobile-arrow {
    width: 24px;
    height: 24px;
    border: 0;
    background: transparent;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex: 0 0 auto;
  }

  .pixso-nav__mobile-arrow span {
    width: 10px;
    height: 10px;
    border-right: 1.5px solid var(--pixso-color-text);
    border-bottom: 1.5px solid var(--pixso-color-text);
    transform: rotate(45deg) translateY(-1px);
    transition: transform 0.2s ease;
    display: block;
  }

  .pixso-nav__mobile-arrow--small {
    width: 20px;
    height: 20px;
  }

  .pixso-nav__mobile-arrow--open span {
    transform: rotate(-135deg) translateY(-1px);
  }

  .pixso-nav__mobile-submenu {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--pixso-gap-xs);
    padding: 0 0 var(--pixso-nav-mobile-sub-row-padding-y) 0;
  }

  .pixso-nav__mobile-submenu--about {
    gap: 18px;
    padding-top: 6px;
  }

  .pixso-nav__mobile-subitem {
    width: 100%;
  }

  .pixso-nav__mobile-children {
    width: 100%;
    padding: 0 0 calc(var(--pixso-gap-sm) / 2) var(--pixso-fs-20);
    display: flex;
    flex-direction: column;
  }

  .pixso-nav__mobile-child-block {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .pixso-nav__mobile-grandchildren {
    width: 100%;
    padding-left: var(--pixso-fs-16);
    display: flex;
    flex-direction: column;
  }
}

@media (max-width: 768px) {
  .pixso-nav__inner {
    width: min(calc(100% - var(--pixso-fs-32)), 100%);
  }

  .pixso-nav__mobile-panel {
    padding-left: var(--pixso-fs-16);
    padding-right: var(--pixso-fs-16);
  }
}
</style>
